import { AppType } from "client/Api/Apps/common";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	inline,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	Checkbox,
	FormItem,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { generateTrackingId, hasFlag } from "client/Misc/utils";
import {
	IInputAttachable
} from "client/Forms/attachable";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { AlertManager, useAlerts } from "client/Hooks";
import {
	EditTeamsAppViewModel,
	SaveTeamsAppModel,
	TeamsAction,
	saveTeamsApp
} from "client/Api/Apps/EditApp/saveApp";

export type EditTeamsAppProps = {
	initialSettings: EditAppSettings<EditTeamsAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditTeamsApp = forwardRef(
	(
		props: EditTeamsAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveTeamsAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const alertManager: AlertManager = useAlerts();

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveTeamsAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveTeamsAppModel>(
			formHandler,
			saveTeamsApp,
			AppType.SharePoint
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const enabledActionsHandler: IInputAttachable<TeamsAction> = formHandler
			.for("enabledActions");

		const addActionHandler = useCallback(
			(action: TeamsAction): void => {
				setFormData(model => ({
					...model,
					enabledActions: model.enabledActions | action
				}));
			},
			[]
		);

		const removeActionHandler = useCallback(
			(action: TeamsAction): void => {
				setFormData(model => ({
					...model,
					enabledActions: model.enabledActions & (~action)
				}));
			},
			[]
		);

		useEffect(
			() => {
				alertManager
					.showInfo(localize("_MakeSureThatOAuth2IsConfiguredAsSingleSignOnForThisApp"));
			},
			[alertManager]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("destinationWindow", "enabledActions")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
				<FormItem label={localize("_EnableActions")}>
					<NoUniqueInputIdContext>
						{actionOptions.map(
							(x) => (
								<ActionCheckbox
									key={x}
									actionKind={x}
									addAction={addActionHandler}
									removeAction={removeActionHandler}
									enabledActions={enabledActionsHandler
										.reducer
										.value
									}
									trackingId={enabledActionsHandler
										.reducer
										.path
									}
								/>
							)
						)}
					</NoUniqueInputIdContext>
					<ValidationError
						{...enabledActionsHandler
							.attachToValidationError()}
					/>
				</FormItem>
			</EditAppBase>
		);
	}
);
EditTeamsApp.displayName = "EditSharePointApp";

const validation = createValidationConfiguration<SaveTeamsAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("enabledActions")
	.add(
		inline((value: TeamsAction): InputValidationResult => {
			if (value === TeamsAction.None) {
				return {
					kind: InputValidationResultKind.Invalid,
					errorMessage: localize("_PleaseSelectAtLeastOneItem")
				};
			}

			return {
				kind: InputValidationResultKind.Valid
			};
		})
	);

type ActionCheckboxProps = {
	enabledActions: TeamsAction;
	actionKind: Exclude<TeamsAction, TeamsAction.None | TeamsAction.All>;
	addAction(action: TeamsAction): void;
	removeAction(action: TeamsAction): void;
	trackingId: string;
};

const ActionCheckbox = (props: ActionCheckboxProps): ReactElement => {
	const {
		actionKind,
		enabledActions,
		addAction,
		removeAction,
		trackingId
	} = props;

	const isChecked = useMemo(
		(): boolean => hasFlag(enabledActions, actionKind),
		[
			actionKind,
			enabledActions
		]
	);

	const changeHandler = useCallback(
		(newValue: boolean): void => {
			if (newValue) {
				addAction(actionKind);
			} else {
				removeAction(actionKind);
			}
		},
		[
			actionKind,
			addAction,
			removeAction
		]
	);

	return (
		<Checkbox
			onChange={changeHandler}
			value={isChecked}
			text={actionKindToDisplayName[actionKind]}
			trackingId={generateTrackingId(trackingId, `|${actionKind}`)}
		/>
	);
};
ActionCheckbox.displayName = "ActionCheckbox";

const actionOptions: readonly Exclude<TeamsAction, TeamsAction.None | TeamsAction.All>[] = [
	TeamsAction.OpenWebClient,
	TeamsAction.OpenApp,
	TeamsAction.OpenLibraryInDocumentsApp
];

const actionKindToDisplayName: {
	[key in Exclude<TeamsAction, TeamsAction.None | TeamsAction.All>]: string;
} = {
	[TeamsAction.OpenWebClient]: localize("_OpenInAppName")
		.replace(
			"{0}",
			localize("_TeamsOnline")
		),
	[TeamsAction.OpenApp]: localize("_OpenInAppName")
		.replace(
			"{0}",
			localize("_Teams")
		),
	[TeamsAction.OpenLibraryInDocumentsApp]: localize("_OpenDocuments")
};

function convertToSaveModel(
	initialSettings: EditAppSettings<EditTeamsAppViewModel>
): SaveTeamsAppModel {
	const {
		destinationWindow,
		enabledActions
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		destinationWindow,
		enabledActions
	};
}